"use client";

// LegalHyper Landing — broadsheet homepage per "The Legal Review" design
import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { StoryImage } from "@/components/StoryImage";
import { AdsterraBanner } from "@/components/ads/AdsterraBanner";
import { ADSTERRA_CONFIG } from "@/config/adsterra";
import { TENANT_CATEGORIES } from "@/config/categories";
import { LEGALHYPER_MOCK_OPINIONS } from "./mockArticles";
import type { MockArticle } from "./mockArticles";
interface Banner {
  id: string;
  imageUrl: string | null;
  linkUrl: string;
  altText: string | null;
  positions: string[];
}
interface Props {
  tenantId: string | null;
  articles: MockArticle[];
  banners: {
    top: Banner[];
    sidebar: Banner[];
    footer: Banner[];
  };
}
function articleHref(article: MockArticle) {
  return `/article/${article.slug || article.id}`;
}

// MediaStack-sourced articles carry a `url` to the original source and open in a
// new tab there — there's no internal page for content LegalHyper doesn't own.
export function ArticleLink(t0) {
  const $ = _c(12);
  const {
    article,
    className,
    style,
    children
  } = t0;
  if (article.url) {
    let t1;
    if ($[0] !== article.url || $[1] !== children || $[2] !== className || $[3] !== style) {
      t1 = <a href={article.url} target="_blank" rel="noopener noreferrer" className={className} style={style}>{children}</a>;
      $[0] = article.url;
      $[1] = children;
      $[2] = className;
      $[3] = style;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== article) {
    t1 = articleHref(article);
    $[5] = article;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children || $[8] !== className || $[9] !== style || $[10] !== t1) {
    t2 = <Link href={t1} className={className} style={style}>{children}</Link>;
    $[7] = children;
    $[8] = className;
    $[9] = style;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
function readingMinutes(content?: string | null) {
  const words = (content ?? "").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
}
const INK = "#0E1A2F";
const INK_DARK = "#0B1424";
const GOLD = "#8A6A22";
const BRASS = "#B08D3F";
const MAROON = "#7A1F2B";
const PARCHMENT = "#F4F0E6";
const RULE = "#DCD5C2";
function Kicker(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      letterSpacing: "0.26em",
      color: GOLD
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <span className="text-[10.5px] font-bold uppercase" style={t1}>{children}</span>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// MediaStack thumbnails come from whatever source outlet — quality varies wildly
// (some are only ~150px wide). This box measures the real pixel size on load and
// never renders the image larger than it actually is: small thumbnails sit at
// native resolution on a panel (sharp, never stretched), full-size images fill
// the design slot and crop with object-cover. A source credit discloses the
// image isn't LegalHyper's own.
function ImagePlaceholder({
  article,
  aspect,
  maxHeight,
  maxWidth = 720,
  smallThreshold = 512,
  onFallback
}: {
  article: MockArticle;
  aspect: string;
  maxHeight?: string;
  maxWidth?: number;
  smallThreshold?: number;
  /** Fires when there's no usable image (missing or failed to load) — lets the
   *  caller drop the article instead of leaving an empty box on the page. */
  onFallback?: () => void;
}) {
  const [nat, setNat] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const [failed, setFailed] = useState(false);
  const isSmall = nat !== null && nat.w > 0 && nat.w < smallThreshold;
  // Cap the rendered width at the image's true pixel width so it is never upscaled.
  const figureMaxWidth = nat ? Math.min(nat.w, maxWidth) : maxWidth;
  // Small images keep their own aspect ratio (no crop); larger ones use the
  // design ratio and crop with object-cover.
  const boxAspect = isSmall && nat ? `${nat.w} / ${nat.h}` : aspect;
  useEffect(() => {
    if (!article.imageUrl || failed) onFallback?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article.imageUrl, failed]);
  if (!article.imageUrl || failed) {
    return <div className="relative w-full overflow-hidden" style={{
      aspectRatio: aspect,
      maxHeight,
      maxWidth,
      background: "#E7E1D0",
      borderLeft: `3px solid ${MAROON}`
    }} />;
  }
  return <figure className="relative m-0 overflow-hidden bg-[#0B1424]" style={{
    width: "100%",
    maxWidth: figureMaxWidth
  }}>
      <div style={{
      position: "relative",
      width: "100%",
      aspectRatio: boxAspect,
      maxHeight
    }}>
        {/* eslint-disable-next-line @next/next/no-img-element -- deliberate: need the true source pixel size, which next/image hides behind the optimizer */}
        <img src={article.imageUrl} alt={article.title} loading="eager" decoding="async" onLoad={e => {
        const el = e.currentTarget;
        if (el.naturalWidth) setNat({
          w: el.naturalWidth,
          h: el.naturalHeight
        });
      }} onError={() => setFailed(true)} style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: isSmall ? "contain" : "cover",
        objectPosition: "center"
      }} />
      </div>
      {article.author && <figcaption className="absolute bottom-2.5 right-2.5 text-[9px] font-bold uppercase px-2 py-1" style={{
      letterSpacing: "0.14em",
      color: "#F4F0E6",
      background: "rgba(11,20,36,0.72)"
    }}>
          {article.author}
        </figcaption>}
    </figure>;
}
function getByCategory(articles: MockArticle[], name: string, n = 3) {
  return articles.filter(a => a.category?.categoryName === name).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, n);
}
export function LegalHyperLanding(t0) {
  const $ = _c(153);
  const {
    articles,
    banners
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [brokenImageIds, setBrokenImageIds] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = id => {
      setBrokenImageIds(prev => prev.has(id) ? prev : new Set(prev).add(id));
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleImageUnavailable = t2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== articles || $[3] !== banners || $[4] !== brokenImageIds) {
    t23 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t24;
      if ($[26] !== brokenImageIds) {
        t24 = a => !!a.imageUrl && !brokenImageIds.has(a.id);
        $[26] = brokenImageIds;
        $[27] = t24;
      } else {
        t24 = $[27];
      }
      const workingArticles = articles.filter(t24);
      if (workingArticles.length === 0) {
        let t25;
        if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
          t25 = {
            background: PARCHMENT
          };
          $[28] = t25;
        } else {
          t25 = $[28];
        }
        let t26;
        if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
          t26 = <p className="text-xl font-semibold mb-2" style={{
            color: INK
          }}>No stories published yet.</p>;
          $[29] = t26;
        } else {
          t26 = $[29];
        }
        let t27;
        if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
          t27 = <div className="min-h-[60vh] flex items-center justify-center px-4 font-chivo" style={t25}><div className="text-center">{t26}<p className="text-sm mt-1" style={{
                color: "#8A8A7C"
              }}>Check back soon for the latest coverage from LegalHyper.</p></div></div>;
          $[30] = t27;
        } else {
          t27 = $[30];
        }
        t23 = t27;
        break bb0;
      }
      const tenantConfig = ADSTERRA_CONFIG.legalhyper;
      const adKeys = tenantConfig.banners;
      const sorted = [...workingArticles].sort(_temp);
      const lead = sorted[0];
      const alsoThisMorning = sorted.slice(1, 6);
      const usedIds = new Set([lead.id, ...alsoThisMorning.map(_temp2)]);
      const latestPool = sorted.filter(a_2 => !usedIds.has(a_2.id));
      const latest = latestPool.slice(0, 5);
      latest.forEach(a_3 => usedIds.add(a_3.id));
      const mostRead = [...sorted].sort(_temp3).slice(0, 8);
      const categories = TENANT_CATEGORIES["legalhyper.com"] ?? [];
      const investigation = sorted.find(_temp4) ?? sorted[0];
      const investigationSeries = sorted.filter(a_6 => a_6.id !== investigation.id).slice(0, 3);
      const editorsPicksPool = sorted.filter(a_7 => !usedIds.has(a_7.id));
      const editorsPicks = (editorsPicksPool.length > 0 ? editorsPicksPool : sorted).slice(0, 3);
      editorsPicks.forEach(a_8 => usedIds.add(a_8.id));
      const docket = [...workingArticles].sort(_temp5).slice(0, 8);
      t20 = "font-chivo";
      if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = {
          background: PARCHMENT,
          color: "#1A1A16",
          minHeight: "100vh"
        };
        $[31] = t21;
      } else {
        t21 = $[31];
      }
      if ($[32] !== banners.top.length) {
        t22 = banners.top.length === 0 && adKeys["728x90"] && <div className="max-w-[1340px] mx-auto px-4 sm:px-7 pt-4 flex justify-center"><div className="hidden sm:block"><AdsterraBanner bannerKey={adKeys["728x90"]} width={728} height={90} /></div>{adKeys["320x50"] && <div className="block sm:hidden"><AdsterraBanner bannerKey={adKeys["320x50"]} width={320} height={50} /></div>}</div>;
        $[32] = banners.top.length;
        $[33] = t22;
      } else {
        t22 = $[33];
      }
      t13 = "max-w-[1340px] mx-auto px-4 sm:px-7";
      let t25;
      if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
        t25 = {
          gap: 40
        };
        $[34] = t25;
      } else {
        t25 = $[34];
      }
      let t26;
      if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = {
          flexBasis: 600
        };
        $[35] = t26;
      } else {
        t26 = $[35];
      }
      let t27;
      if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t27 = <span className="flex-1 h-px" style={{
          background: BRASS
        }} />;
        $[36] = t27;
      } else {
        t27 = $[36];
      }
      let t28;
      if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
        t28 = {
          fontSize: "clamp(30px,4.4vw,54px)",
          lineHeight: 1.06,
          letterSpacing: "-0.005em",
          color: INK
        };
        $[37] = t28;
      } else {
        t28 = $[37];
      }
      const t29 = <ImagePlaceholder article={lead} aspect="16/9" maxHeight="clamp(200px, 30vw, 340px)" onFallback={() => handleImageUnavailable(lead.id)} />;
      let t30;
      if ($[38] !== lead || $[39] !== t29) {
        t30 = <ArticleLink article={lead} className="block mt-6">{t29}</ArticleLink>;
        $[38] = lead;
        $[39] = t29;
        $[40] = t30;
      } else {
        t30 = $[40];
      }
      let t31;
      let t32;
      if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
        t31 = {
          letterSpacing: "0.12em",
          color: "#7A7466",
          borderTop: `1px solid ${RULE}`
        };
        t32 = {
          color: "#1A1A16",
          fontWeight: 600
        };
        $[41] = t31;
        $[42] = t32;
      } else {
        t31 = $[41];
        t32 = $[42];
      }
      let t33;
      if ($[43] !== lead) {
        t33 = <span style={t32}>By {lead.author}</span>;
        $[43] = lead;
        $[44] = t33;
      } else {
        t33 = $[44];
      }
      let t34;
      if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
        t34 = <span className="w-px h-2.5" style={{
          background: "#CBC4B1"
        }} />;
        $[45] = t34;
      } else {
        t34 = $[45];
      }
      let t35;
      if ($[46] !== lead) {
        t35 = formatDate(lead.createdAt);
        $[46] = lead;
        $[47] = t35;
      } else {
        t35 = $[47];
      }
      let t36;
      if ($[48] !== t35) {
        t36 = <span>{t35}</span>;
        $[48] = t35;
        $[49] = t36;
      } else {
        t36 = $[49];
      }
      let t37;
      if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
        t37 = <span className="w-px h-2.5" style={{
          background: "#CBC4B1"
        }} />;
        $[50] = t37;
      } else {
        t37 = $[50];
      }
      let t38;
      if ($[51] !== lead) {
        t38 = readingMinutes(lead.content);
        $[51] = lead;
        $[52] = t38;
      } else {
        t38 = $[52];
      }
      let t39;
      if ($[53] !== t38) {
        t39 = <span>{t38} min read</span>;
        $[53] = t38;
        $[54] = t39;
      } else {
        t39 = $[54];
      }
      let t40;
      if ($[55] !== t33 || $[56] !== t36 || $[57] !== t39) {
        t40 = <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-5 pt-4 text-[11px] uppercase" style={t31}>{t33}{t34}{t36}{t37}{t39}</div>;
        $[55] = t33;
        $[56] = t36;
        $[57] = t39;
        $[58] = t40;
      } else {
        t40 = $[58];
      }
      let t41;
      if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
        t41 = {
          flexBasis: 290
        };
        $[59] = t41;
      } else {
        t41 = $[59];
      }
      let t42;
      if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
        t42 = {
          borderTop: `1px solid ${INK}`,
          paddingTop: 18
        };
        $[60] = t42;
      } else {
        t42 = $[60];
      }
      let t43;
      if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
        t43 = <div className="pt-4.5" style={t42}><div className="text-[10px] font-bold uppercase" style={{
            letterSpacing: "0.26em",
            color: GOLD
          }}>Also this morning</div></div>;
        $[61] = t43;
      } else {
        t43 = $[61];
      }
      let t44;
      let t45;
      if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
        t44 = {
          borderTop: `1px solid ${RULE}`,
          paddingTop: 22
        };
        t45 = {
          borderLeft: `2px solid ${MAROON}`,
          paddingLeft: 14
        };
        $[62] = t44;
        $[63] = t45;
      } else {
        t44 = $[62];
        t45 = $[63];
      }
      let t46;
      if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
        t46 = <div className="text-[10px] font-bold uppercase" style={{
          letterSpacing: "0.24em",
          color: MAROON
        }}>Legal Alert</div>;
        $[64] = t46;
      } else {
        t46 = $[64];
      }
      let t47;
      if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
        t47 = <div style={t44}><div style={t45}>{t46}<div className="font-garamond mt-2" style={{
              fontSize: 18,
              lineHeight: 1.3,
              color: "#1A1A16"
            }}>Legal Geek London registration closes for early-bird passes at the end of this week.</div></div></div>;
        $[65] = t47;
      } else {
        t47 = $[65];
      }
      const t48 = <div className="flex flex-wrap gap-10 sm:gap-13" style={t25}><div className="flex-1 min-w-0" style={t26}><div className="flex items-center gap-3.5"><Kicker>{lead.category?.categoryName}</Kicker>{t27}</div><ArticleLink article={lead}><h1 className="font-bodoni font-medium uppercase m-0 mt-5" style={t28}>{lead.title}</h1></ArticleLink>{t30}{lead.content && <p className="font-garamond text-[19px] leading-[1.58] max-w-[58ch] mt-5" style={{
            color: "#3B3B33"
          }}>{lead.content}</p>}{t40}</div><div className="flex-1 min-w-0 flex flex-col gap-6" style={t41}>{t43}{alsoThisMorning.map(_temp6)}{t47}</div></div>;
      if ($[66] !== t48) {
        t14 = <section className="pt-11">{t48}</section>;
        $[66] = t48;
        $[67] = t14;
      } else {
        t14 = $[67];
      }
      let t49;
      if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
        t49 = {
          borderTop: `3px double ${INK}`,
          paddingTop: 44,
          gap: 44
        };
        $[68] = t49;
      } else {
        t49 = $[68];
      }
      let t50;
      if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
        t50 = {
          flexBasis: 620
        };
        $[69] = t50;
      } else {
        t50 = $[69];
      }
      let t51;
      if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
        t51 = {
          borderBottom: `1px solid ${INK}`
        };
        $[70] = t51;
      } else {
        t51 = $[70];
      }
      let t52;
      if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
        t52 = <h2 className="font-garamond font-semibold m-0" style={{
          fontSize: 30,
          color: INK,
          letterSpacing: "0.02em"
        }}>Legal AI News</h2>;
        $[71] = t52;
      } else {
        t52 = $[71];
      }
      let t53;
      if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
        t53 = <div className="flex items-baseline justify-between gap-5 pb-4" style={t51}>{t52}<Link href="/search" className="text-[10.5px] uppercase shrink-0" style={{
            letterSpacing: "0.2em",
            color: GOLD
          }}>All coverage →</Link></div>;
        $[72] = t53;
      } else {
        t53 = $[72];
      }
      let t54;
      if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
        t54 = article_0 => <article key={article_0.id} className="flex flex-wrap gap-6.5 py-7" style={{
          borderBottom: `1px solid ${RULE}`,
          gap: 26
        }}><ArticleLink article={article_0} className="shrink-0" style={{
            flexBasis: 220,
            maxWidth: "100%"
          }}><div className="relative w-full aspect-[4/3] overflow-hidden bg-[#E3DECF]"><StoryImage src={article_0.imageUrl} alt={article_0.title} fill={true} className="object-cover" sizes="220px" onFallback={() => handleImageUnavailable(article_0.id)} /></div></ArticleLink><div className="flex-1 min-w-0" style={{
            flexBasis: 280
          }}><Kicker>{article_0.category?.categoryName}</Kicker><ArticleLink article={article_0}><h3 className="font-garamond font-semibold mt-2.5 mb-0" style={{
                fontSize: 25,
                lineHeight: 1.2,
                color: INK
              }}>{article_0.title}</h3></ArticleLink>{article_0.content && <p className="text-[15px] leading-[1.6] mt-2.5" style={{
              color: "#4E4E45"
            }}>{article_0.content}</p>}<div className="mt-3 text-[10.5px] uppercase" style={{
              letterSpacing: "0.12em",
              color: "#7A7466"
            }}>{article_0.author} · {formatDate(article_0.createdAt)}</div></div></article>;
        $[73] = t54;
      } else {
        t54 = $[73];
      }
      let t55;
      if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
        t55 = <div className="pt-8"><Link href="/search" className="inline-block text-[10.5px] uppercase px-7 py-3" style={{
            border: `1px solid ${INK}`,
            color: INK,
            letterSpacing: "0.2em"
          }}>More legal news</Link></div>;
        $[74] = t55;
      } else {
        t55 = $[74];
      }
      let t56;
      if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
        t56 = {
          flexBasis: 290
        };
        $[75] = t56;
      } else {
        t56 = $[75];
      }
      let t57;
      if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
        t57 = {
          borderBottom: `1px solid ${INK}`
        };
        $[76] = t57;
      } else {
        t57 = $[76];
      }
      let t58;
      if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
        t58 = <div className="pb-4" style={t57}><h2 className="font-garamond font-semibold m-0" style={{
            fontSize: 30,
            color: INK,
            letterSpacing: "0.02em"
          }}>Most Read</h2></div>;
        $[77] = t58;
      } else {
        t58 = $[77];
      }
      let t59;
      if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
        t59 = {
          background: "#EFEADC",
          border: `1px solid ${RULE}`,
          borderTop: `2px solid ${BRASS}`
        };
        $[78] = t59;
      } else {
        t59 = $[78];
      }
      let t60;
      if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
        t60 = <div className="text-[10px] font-bold uppercase" style={{
          letterSpacing: "0.24em",
          color: GOLD
        }}>On the Docket This Week</div>;
        $[79] = t60;
      } else {
        t60 = $[79];
      }
      let t61;
      let t62;
      if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
        t61 = <div className="mt-9 p-6" style={t59}>{t60}<div className="flex flex-col gap-3.5 mt-4">{[{
              label: "Legal AI keynote",
              when: "Day 1 \xB7 Main Stage"
            }, {
              label: "Regulation & Policy panel",
              when: "Day 1 \xB7 Track B"
            }, {
              label: "Courts & Litigation briefing",
              when: "Day 2 \xB7 Track A"
            }].map(_temp8)}</div></div>;
        t62 = adKeys["300x250"] && <div className="mt-8 flex justify-center pt-6" style={{
          borderTop: `1px solid ${RULE}`
        }}><AdsterraBanner bannerKey={adKeys["300x250"]} width={300} height={250} /></div>;
        $[80] = t61;
        $[81] = t62;
      } else {
        t61 = $[80];
        t62 = $[81];
      }
      t15 = <section className="flex flex-wrap gap-10 sm:gap-13 mt-14" style={t49}><div className="flex-1 min-w-0" style={t50}>{t53}{latest.map(t54)}{t55}</div><aside className="flex-1 min-w-0" style={t56}>{t58}{mostRead.map(_temp7)}{t61}{t62}</aside></section>;
      let t63;
      if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
        t63 = {
          borderTop: `3px double ${INK}`
        };
        $[82] = t63;
      } else {
        t63 = $[82];
      }
      let t64;
      if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
        t64 = <h2 className="font-garamond font-semibold m-0" style={{
          fontSize: 32,
          color: INK,
          letterSpacing: "0.02em"
        }}>Featured Desks</h2>;
        $[83] = t64;
      } else {
        t64 = $[83];
      }
      let t65;
      if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
        t65 = <div className="flex items-baseline justify-between gap-5 pb-5">{t64}<div className="text-[10.5px] uppercase" style={{
            letterSpacing: "0.2em",
            color: "#7A7466"
          }}>Six desks · updated hourly</div></div>;
        $[84] = t65;
      } else {
        t65 = $[84];
      }
      let t66;
      if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
        t66 = {
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))"
        };
        $[85] = t66;
      } else {
        t66 = $[85];
      }
      t16 = <section className="pt-16 mt-14" style={t63}>{t65}<div className="grid gap-x-11 gap-y-0" style={t66}>{categories.map(category => {
            const items = getByCategory(workingArticles, category, 3);
            if (items.length === 0) {
              return null;
            }
            return <div key={category} className="pt-5 pb-8" style={{
              borderTop: `1px solid ${INK}`
            }}><h3 className="font-garamond font-semibold m-0" style={{
                fontSize: 21,
                color: INK
              }}>{category}</h3><div className="flex flex-col gap-3.5 mt-4.5" style={{
                marginTop: 18
              }}>{items.map(_temp9)}</div></div>;
          })}</div></section>;
      t17 = editorsPicks.length > 0 && <section className="pt-16 mt-14" style={{
        borderTop: `3px double ${INK}`
      }}><div className="flex items-baseline justify-between gap-5 pb-5" style={{
          borderBottom: `1px solid ${INK}`
        }}><h2 className="font-garamond font-semibold m-0" style={{
            fontSize: 32,
            color: INK,
            letterSpacing: "0.02em"
          }}>Editor's Picks</h2><div className="text-[10.5px] uppercase" style={{
            letterSpacing: "0.2em",
            color: "#7A7466"
          }}>Hand-selected coverage</div></div><div className="grid gap-9 pt-9" style={{
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))"
        }}>{editorsPicks.map(article_2 => <article key={article_2.id}><ArticleLink article={article_2} className="block"><div className="relative w-full overflow-hidden" style={{
                aspectRatio: "4/3",
                background: "#E3DECF"
              }}><StoryImage src={article_2.imageUrl} alt={article_2.title} fill={true} className="object-cover" sizes="(min-width: 1024px) 33vw, 100vw" onFallback={() => handleImageUnavailable(article_2.id)} /></div></ArticleLink><div className="mt-4"><Kicker>{article_2.category?.categoryName}</Kicker></div><ArticleLink article={article_2}><h3 className="font-bodoni font-medium uppercase mt-2.5 mb-0" style={{
                fontSize: 23,
                lineHeight: 1.2,
                color: INK
              }}>{article_2.title}</h3></ArticleLink>{article_2.content && <p className="font-garamond text-[15.5px] leading-[1.55] mt-2.5" style={{
              color: "#4E4E45"
            }}>{article_2.content}</p>}<div className="mt-3 text-[10.5px] uppercase" style={{
              letterSpacing: "0.12em",
              color: "#7A7466"
            }}>{article_2.author} · {formatDate(article_2.createdAt)}</div></article>)}</div></section>;
      t18 = docket.length > 0 && <section className="mt-14 py-6" style={{
        borderTop: `1px solid ${INK}`,
        borderBottom: `1px solid ${INK}`
      }}><div className="flex items-center gap-7 overflow-x-auto" style={{
          scrollbarWidth: "none"
        }}><span className="shrink-0 text-[10px] font-bold uppercase px-3 py-1.5" style={{
            letterSpacing: "0.2em",
            color: PARCHMENT,
            background: MAROON
          }}>Latest Filings</span>{docket.map(_temp10)}</div></section>;
      let t67;
      if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
        t67 = {
          background: "#EFEADC",
          borderTop: `1px solid ${RULE}`,
          borderBottom: `1px solid ${RULE}`
        };
        $[86] = t67;
      } else {
        t67 = $[86];
      }
      let t68;
      if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
        t68 = {
          borderBottom: `1px solid ${INK}`
        };
        $[87] = t68;
      } else {
        t68 = $[87];
      }
      let t69;
      if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
        t69 = <h2 className="font-garamond font-semibold m-0" style={{
          fontSize: 32,
          color: INK,
          letterSpacing: "0.02em"
        }}>{"Opinion & Analysis"}</h2>;
        $[88] = t69;
      } else {
        t69 = $[88];
      }
      let t70;
      if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
        t70 = <div className="flex items-baseline justify-between gap-5 pb-5" style={t68}>{t69}<div className="text-[10px] font-bold uppercase" style={{
            letterSpacing: "0.24em",
            color: GOLD
          }}>Expert Analysis</div></div>;
        $[89] = t70;
      } else {
        t70 = $[89];
      }
      if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = <section className="mt-14" style={t67}><div className="max-w-[1340px] mx-auto px-4 sm:px-7 py-16">{t70}<div className="grid gap-9 pt-9" style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))"
            }}>{LEGALHYPER_MOCK_OPINIONS.map(_temp11)}</div></div></section>;
        t11 = {
          background: INK_DARK,
          color: "#EDE9DE"
        };
        $[90] = t11;
        $[91] = t19;
      } else {
        t11 = $[90];
        t19 = $[91];
      }
      t12 = "-mx-4 sm:-mx-7";
      t9 = "max-w-[1340px] mx-auto px-4 sm:px-7 py-16";
      let t71;
      if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
        t71 = <span className="text-[10.5px] font-bold uppercase" style={{
          letterSpacing: "0.28em",
          color: BRASS
        }}>Investigation</span>;
        $[92] = t71;
      } else {
        t71 = $[92];
      }
      let t72;
      if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
        t72 = <span className="flex-1 h-px" style={{
          background: "#26314A"
        }} />;
        $[93] = t72;
      } else {
        t72 = $[93];
      }
      if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <div className="flex items-center gap-4 pb-8">{t71}{t72}<span className="text-[10.5px] uppercase" style={{
            letterSpacing: "0.18em",
            color: "#8E97A8"
          }}>Ongoing series</span></div>;
        $[94] = t10;
      } else {
        t10 = $[94];
      }
      t7 = "flex flex-wrap gap-11";
      let t73;
      if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
        t73 = {
          flexBasis: 540
        };
        $[95] = t73;
      } else {
        t73 = $[95];
      }
      let t74;
      if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
        t74 = {
          aspectRatio: "3/2",
          background: "repeating-linear-gradient(135deg,#16223A 0 10px,#1C2A46 10px 20px)"
        };
        $[96] = t74;
      } else {
        t74 = $[96];
      }
      let t75;
      if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
        t75 = {
          fontSize: "clamp(26px,3.2vw,40px)",
          lineHeight: 1.12,
          color: "#F4F0E6"
        };
        $[97] = t75;
      } else {
        t75 = $[97];
      }
      let t76;
      if ($[98] !== investigation) {
        t76 = <h2 className="font-bodoni font-medium uppercase mt-7" style={t75}>{investigation.title}</h2>;
        $[98] = investigation;
        $[99] = t76;
      } else {
        t76 = $[99];
      }
      let t77;
      let t78;
      if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
        t77 = {
          letterSpacing: "0.12em",
          color: "#8E97A8",
          borderTop: "1px solid #26314A"
        };
        t78 = {
          color: "#EDE9DE",
          fontWeight: 600
        };
        $[100] = t77;
        $[101] = t78;
      } else {
        t77 = $[100];
        t78 = $[101];
      }
      let t79;
      if ($[102] !== investigation) {
        t79 = <span style={t78}>By {investigation.author}</span>;
        $[102] = investigation;
        $[103] = t79;
      } else {
        t79 = $[103];
      }
      let t80;
      if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
        t80 = <span className="w-px h-2.5" style={{
          background: "#3A465F"
        }} />;
        $[104] = t80;
      } else {
        t80 = $[104];
      }
      let t81;
      if ($[105] !== investigation) {
        t81 = formatDate(investigation.createdAt);
        $[105] = investigation;
        $[106] = t81;
      } else {
        t81 = $[106];
      }
      let t82;
      if ($[107] !== t81) {
        t82 = <span>{t81}</span>;
        $[107] = t81;
        $[108] = t82;
      } else {
        t82 = $[108];
      }
      let t83;
      if ($[109] !== t79 || $[110] !== t82) {
        t83 = <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-5 pt-4 text-[11px] uppercase" style={t77}>{t79}{t80}{t82}</div>;
        $[109] = t79;
        $[110] = t82;
        $[111] = t83;
      } else {
        t83 = $[111];
      }
      t8 = <div className="flex-1 min-w-0" style={t73}><ArticleLink article={investigation}><div className="relative w-full overflow-hidden" style={t74}><StoryImage src={investigation.imageUrl} alt={investigation.title} fill={true} className="object-cover" sizes="600px" onFallback={() => handleImageUnavailable(investigation.id)} /></div></ArticleLink>{t76}{investigation.content && <p className="font-garamond text-[18px] leading-[1.6] max-w-[62ch] mt-4.5" style={{
          color: "#C3C9D6"
        }}>{investigation.content}</p>}{t83}</div>;
      t3 = "flex-1 min-w-0 flex flex-col";
      if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = {
          flexBasis: 300
        };
        $[112] = t4;
      } else {
        t4 = $[112];
      }
      if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <div className="text-[10px] font-bold uppercase pb-3.5" style={{
          letterSpacing: "0.26em",
          color: BRASS,
          borderBottom: "1px solid #26314A"
        }}>More from the series</div>;
        $[113] = t5;
      } else {
        t5 = $[113];
      }
      t6 = investigationSeries.map(_temp12);
    }
    $[2] = articles;
    $[3] = banners;
    $[4] = brokenImageIds;
    $[5] = t10;
    $[6] = t11;
    $[7] = t12;
    $[8] = t13;
    $[9] = t14;
    $[10] = t15;
    $[11] = t16;
    $[12] = t17;
    $[13] = t18;
    $[14] = t19;
    $[15] = t20;
    $[16] = t21;
    $[17] = t22;
    $[18] = t23;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t10 = $[5];
    t11 = $[6];
    t12 = $[7];
    t13 = $[8];
    t14 = $[9];
    t15 = $[10];
    t16 = $[11];
    t17 = $[12];
    t18 = $[13];
    t19 = $[14];
    t20 = $[15];
    t21 = $[16];
    t22 = $[17];
    t23 = $[18];
    t3 = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  if (t23 !== Symbol.for("react.early_return_sentinel")) {
    return t23;
  }
  let t24;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Link href="/search" className="mt-6 inline-block text-center px-6 py-3 text-[10.5px] uppercase" style={{
      border: `1px solid ${BRASS}`,
      color: BRASS,
      letterSpacing: "0.2em"
    }}>All investigations</Link>;
    $[114] = t24;
  } else {
    t24 = $[114];
  }
  let t25;
  if ($[115] !== t3 || $[116] !== t4 || $[117] !== t5 || $[118] !== t6) {
    t25 = <div className={t3} style={t4}>{t5}{t6}{t24}</div>;
    $[115] = t3;
    $[116] = t4;
    $[117] = t5;
    $[118] = t6;
    $[119] = t25;
  } else {
    t25 = $[119];
  }
  let t26;
  if ($[120] !== t25 || $[121] !== t7 || $[122] !== t8) {
    t26 = <div className={t7}>{t8}{t25}</div>;
    $[120] = t25;
    $[121] = t7;
    $[122] = t8;
    $[123] = t26;
  } else {
    t26 = $[123];
  }
  let t27;
  if ($[124] !== t10 || $[125] !== t26 || $[126] !== t9) {
    t27 = <div className={t9}>{t10}{t26}</div>;
    $[124] = t10;
    $[125] = t26;
    $[126] = t9;
    $[127] = t27;
  } else {
    t27 = $[127];
  }
  let t28;
  if ($[128] !== t11 || $[129] !== t12 || $[130] !== t27) {
    t28 = <section style={t11} className={t12}>{t27}</section>;
    $[128] = t11;
    $[129] = t12;
    $[130] = t27;
    $[131] = t28;
  } else {
    t28 = $[131];
  }
  let t29;
  if ($[132] !== t13 || $[133] !== t14 || $[134] !== t15 || $[135] !== t16 || $[136] !== t17 || $[137] !== t18 || $[138] !== t19 || $[139] !== t28) {
    t29 = <main className={t13}>{t14}{t15}{t16}{t17}{t18}{t19}{t28}</main>;
    $[132] = t13;
    $[133] = t14;
    $[134] = t15;
    $[135] = t16;
    $[136] = t17;
    $[137] = t18;
    $[138] = t19;
    $[139] = t28;
    $[140] = t29;
  } else {
    t29 = $[140];
  }
  let t30;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      background: PARCHMENT
    };
    $[141] = t30;
  } else {
    t30 = $[141];
  }
  let t31;
  if ($[142] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="text-[10px] font-bold uppercase" style={{
      letterSpacing: "0.28em",
      color: GOLD
    }}>Newsletter</div>;
    $[142] = t31;
  } else {
    t31 = $[142];
  }
  let t32;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <h2 className="font-bodoni font-medium uppercase mt-4.5" style={{
      fontSize: "clamp(30px,4vw,46px)",
      lineHeight: 1.12,
      color: INK
    }}>Stay Ahead of Legal AI</h2>;
    $[143] = t32;
  } else {
    t32 = $[143];
  }
  let t33;
  if ($[144] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <p className="font-garamond text-[18px] leading-[1.55] max-w-[56ch] mx-auto mt-4.5" style={{
      color: "#3B3B33"
    }}>Get the day's most important legal AI news, product launches, and policy developments delivered to your inbox.</p>;
    $[144] = t33;
  } else {
    t33 = $[144];
  }
  let t34;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <input placeholder="your@email.com" className="flex-1 min-w-[260px] bg-transparent outline-none text-[15px]" style={{
      border: "1px solid #CBC4B1",
      padding: "15px 16px",
      color: "#1A1A16"
    }} />;
    $[145] = t34;
  } else {
    t34 = $[145];
  }
  let t35;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <form onSubmit={_temp13} className="flex flex-wrap gap-3 justify-center mt-8 max-w-[560px] mx-auto">{t34}<button type="submit" className="shrink-0 text-[10.5px] uppercase" style={{
        background: INK,
        color: PARCHMENT,
        border: "none",
        padding: "15px 30px",
        letterSpacing: "0.18em"
      }}>Subscribe</button></form>;
    $[146] = t35;
  } else {
    t35 = $[146];
  }
  let t36;
  if ($[147] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <section style={t30}><div className="max-w-[900px] mx-auto px-4 sm:px-7 py-20 text-center">{t31}{t32}{t33}{t35}<p className="text-[12px] leading-[1.6] mx-auto mt-5 max-w-[46ch]" style={{
          color: "#7A7466"
        }}>We use your email only to send the LegalHyper briefing. Unsubscribe at any time.</p></div></section>;
    $[147] = t36;
  } else {
    t36 = $[147];
  }
  let t37;
  if ($[148] !== t20 || $[149] !== t21 || $[150] !== t22 || $[151] !== t29) {
    t37 = <div className={t20} style={t21}>{t22}{t29}{t36}</div>;
    $[148] = t20;
    $[149] = t21;
    $[150] = t22;
    $[151] = t29;
    $[152] = t37;
  } else {
    t37 = $[152];
  }
  return t37;
}
function _temp13(e) {
  return e.preventDefault();
}
function _temp12(a_11) {
  return <ArticleLink key={a_11.id} article={a_11} className="py-5" style={{
    color: "#EDE9DE",
    borderBottom: "1px solid #26314A"
  }}><div className="font-garamond" style={{
      fontSize: 21,
      lineHeight: 1.26
    }}>{a_11.title}</div><div className="text-[11px] uppercase mt-2" style={{
      letterSpacing: "0.1em",
      color: "#8E97A8"
    }}>{readingMinutes(a_11.content)} min read</div></ArticleLink>;
}
function _temp11(op) {
  return <article key={op.id}><div className="w-24 h-24 rounded-full mb-4.5 flex items-center justify-center" style={{
      background: "repeating-linear-gradient(135deg,#D6D0C1 0 8px,#DFDACB 8px 16px)",
      marginBottom: 18
    }} /><h3 className="font-garamond font-semibold m-0" style={{
      fontSize: 24,
      lineHeight: 1.24,
      color: INK
    }}>{op.title}</h3><p className="text-[15px] leading-[1.6] mt-3.5" style={{
      color: "#4E4E45"
    }}>{op.dek}</p><div className="mt-4 pt-3.5" style={{
      borderTop: `1px solid ${RULE}`
    }}><div className="text-[13px] font-semibold uppercase" style={{
        letterSpacing: "0.06em",
        color: "#1A1A16"
      }}>{op.author}</div><div className="text-[12.5px] mt-1" style={{
        color: "#7A7466"
      }}>{op.credential}</div></div></article>;
}
function _temp10(article_3, i_2) {
  return <ArticleLink key={article_3.id} article={article_3} className="shrink-0 flex items-center gap-3" style={i_2 > 0 ? {
    borderLeft: `1px solid ${RULE}`,
    paddingLeft: 28
  } : undefined}><span className="text-[10.5px] font-bold uppercase" style={{
      letterSpacing: "0.1em",
      color: GOLD
    }}>{new Date(article_3.createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      }).toUpperCase()}</span><span className="font-garamond text-[15px] whitespace-nowrap" style={{
      color: INK
    }}>{article_3.title}</span></ArticleLink>;
}
function _temp9(a_10, i_1) {
  return <ArticleLink key={a_10.id} article={a_10} className="text-[15px] leading-[1.45] transition-colors hover:text-[#8A6A22]" style={{
    color: "#1A1A16",
    borderTop: i_1 > 0 ? "1px solid #E6E0D0" : "none",
    paddingTop: i_1 > 0 ? 14 : 0
  }}>{a_10.title}</ArticleLink>;
}
function _temp8(item) {
  return <div key={item.label} className="flex gap-3" style={{
    borderTop: "1px solid #DFD9C8",
    paddingTop: 13
  }}><div className="text-[11px] shrink-0" style={{
      color: "#7A7466",
      flexBasis: 50,
      paddingTop: 3,
      letterSpacing: "0.06em"
    }}>{item.when.split(" \xB7 ")[0]}</div><div className="font-garamond text-[16px] leading-[1.4]" style={{
      color: "#1A1A16"
    }}>{item.label} — <em>{item.when.split(" \xB7 ")[1]}</em></div></div>;
}
function _temp7(article_1, i_0) {
  return <div key={article_1.id} className="flex gap-4.5 py-5" style={{
    borderBottom: `1px solid ${RULE}`,
    gap: 18
  }}><div className="font-bodoni shrink-0" style={{
      fontSize: 36,
      lineHeight: 0.9,
      color: "#C3BCA7"
    }}>{String(i_0 + 1).padStart(2, "0")}</div><ArticleLink article={article_1} className="font-garamond" style={{
      fontSize: 19,
      lineHeight: 1.3,
      color: INK
    }}>{article_1.title}</ArticleLink></div>;
}
function _temp6(article, i) {
  return <div key={article.id} style={i > 0 ? {
    borderTop: `1px solid ${RULE}`,
    paddingTop: 22
  } : undefined}><ArticleLink article={article}><h2 className="font-garamond font-semibold m-0" style={{
        fontSize: 22,
        lineHeight: 1.24,
        color: INK
      }}>{article.title}</h2></ArticleLink><div className="mt-2.5 text-[10.5px] uppercase" style={{
      letterSpacing: "0.12em",
      color: "#7A7466"
    }}>{article.author} · {readingMinutes(article.content)} min</div></div>;
}
function _temp5(a_9, b_1) {
  return new Date(b_1.createdAt).getTime() - new Date(a_9.createdAt).getTime();
}
function _temp4(a_5) {
  return a_5.category?.categoryName === "Legal Geek Coverage";
}
function _temp3(a_4, b_0) {
  return (b_0.trendingScore ?? 0) - (a_4.trendingScore ?? 0);
}
function _temp2(a_1) {
  return a_1.id;
}
function _temp(a_0, b) {
  if ((b.trendingScore ?? 0) !== (a_0.trendingScore ?? 0)) {
    return (b.trendingScore ?? 0) - (a_0.trendingScore ?? 0);
  }
  return new Date(b.createdAt).getTime() - new Date(a_0.createdAt).getTime();
}